import React, { Component } from 'react';
import { AreaChart, Area, BarChart, Bar, CartesianGrid, Legend, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { DashboardAPI } from '../../../services/dashboardService';
import { isAllDealerships } from '../../../util/locationUtils';
import { isInstallerAdmin } from '../../../util/permissions';
import ScopeService from '../../../services/scopeService';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import DonutChart from './DonutChart';
import { chartColor, chartColors } from './chartColors';
import { buildLeadSourceSlices, buildLeadSourceMonths } from './leadSources';
import { themeColor } from '../../../util/theme';
import ReactGA from 'react-ga4';
import './Dashboard.css';

const USE_MOCK_DATA = false;

class Dashboard extends Component {
  state = {
    loading: false,
    error: null,
    data: null,
    period: 30,
    isDesktop: window.innerWidth >= 1024
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.loadData();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.setState({ isDesktop: window.innerWidth >= 1024 });
  };

  loadData = async () => {
    if (USE_MOCK_DATA) {
      this.setState({ data: this.getMockData(), loading: false });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const { typeId, userType, company } = this.props;
      let dealerId = null;
      let installerId = null;
      let groupId = null;

      // Dealer-group access (user_companies.location_id = 0) loads the whole
      // group; sub-scoped dealer users behave like a single-dealership user.
      if (userType === 'dealer' && ScopeService.isDealerGroupUser()) {
        groupId = this.props.groupId;
        dealerId = isAllDealerships(this.props.selectedDealerId) ? null : this.props.selectedDealerId;
      } else if (userType === 'dealer') {
        dealerId = typeId;
        installerId = company?.installer_id;
      } else {
        installerId = typeId;
      }

      const locationId = this.props.selectedLocationId || null;
      const data = await DashboardAPI.getMetrics(installerId, this.state.period, dealerId, locationId, groupId);
      console.log('[Dashboard] API response RAW:', JSON.stringify(data, null, 2));
      this.setState({ data, loading: false });
    } catch (err) {
      console.error('[Dashboard] Error loading metrics:', err);
      this.setState({ error: 'Unable to load dashboard data. Please try again.', loading: false });
    }
  };

  handlePeriodChange = (days) => {
    this.setState({ period: days }, this.loadData);
    ReactGA.event({ category: 'Dashboard', action: 'Changed Period', label: `${days}d` });
  };

  // --- Installer (Specialist) View ---
  renderInstallerDashboard() {
    const { data } = this.state;
    if (!data) return null;

    const { aggregate, outstanding, payments, dailyTrend, leadAttribution, campaigns, customers } = data;
    const user = this.props.user || {};
    const isOwner = isInstallerAdmin(user);

    // 'inventory' | 'usage' | 'none'. Absent reads as 'inventory' to match what
    // the server reports when the column is unmigrated or the probe fails —
    // which is also what every client assumed before this field existed.
    const materialsMode = (data.inventory && data.inventory.materialsTrackingMode) || 'inventory';
    // Stock levels only mean something where stock is maintained.
    const showStockAlerts = materialsMode === 'inventory';
    // Consumption is worth showing to a usage shop too — that is the whole point
    // of the mode. Still length-gated, so it stays hidden until the server
    // re-sources it from the capture path rather than inventory transactions.
    const hasMaterialsUsed = ((data.inventory && data.inventory.topMaterialsUsed) || []).length > 0;

    // Convert revenueBySource object to array for donut chart
    const sourceData = this.objectToArray(data.revenueBySource, 'source', { jobs: 'count', revenue: 'value' });

    // Convert serviceBreakdown for donut chart
    const serviceData = (data.serviceBreakdown || []).slice(0, 5).map(s => ({
      name: s.service_name,
      value: s.total_revenue
    }));

    // Convert payments.byMethod object to array — map known method keys to display labels
    const PAYMENT_METHOD_LABELS = {
      'Unknown': 'Klarna',
      'card': 'Card',
      'us_bank_account': 'Bank Transfer',
      'cashapp': 'Cash App',
      'Check': 'Check',
      'check': 'Check',
    };
    const paymentData = this.objectToArray(payments?.byMethod, 'method', { count: 'count', amount: 'value' })
      .map(p => ({ ...p, method: PAYMENT_METHOD_LABELS[p.method] || p.method }));

    // Build aging buckets from outstanding.jobs
    const agingBuckets = this.buildAgingBuckets(outstanding?.jobs || []);

    // Top lead source
    const topSource = leadAttribution?.bySource
      ? Object.entries(leadAttribution.bySource).sort((a, b) => b[1].revenue - a[1].revenue)[0]
      : null;

    // Lead source data for donut — fixed colour per source, shared with the trend
    const leadSourceData = buildLeadSourceSlices(leadAttribution?.bySource)
      .map(({ colorKey, ...slice }) => ({ ...slice, color: chartColor(colorKey) }));

    // Leads per month by source. One month is not a trend, so it needs >= 2.
    const leadTrend = buildLeadSourceMonths(leadAttribution?.byMonth);

    // Campaign data for donut
    const campaignData = (campaigns || []).map(c => ({ name: c.campaign_name, value: c.revenue }));

    return (
      <>
        {/* Top-Level KPIs */}
        <div className="overview-cards">
          <StatCard
            label="Leads"
            value={aggregate.leads.thisMonth}
            sublabel="this month"
            sparkData={this.buildSparkFromTrend(dailyTrend, 'jobs')}
          />
          <StatCard
            label="Booked Revenue"
            value={this.formatCurrency(aggregate.bookedRevenue)}
          />
          <StatCard
            label="Collected"
            value={this.formatCurrency(payments?.totalPaid || 0)}
            sublabel="this period"
            sparkData={this.buildSparkFromTrend(dailyTrend, 'revenue')}
          />
          <StatCard
            label="Outstanding"
            value={`${outstanding?.count || 0} / ${this.formatCurrency(outstanding?.totalRevenue || 0)}`}
            className={(outstanding?.count || 0) > 0 ? 'warning' : ''}
          />
          <StatCard
            label="Avg Ticket"
            value={this.formatCurrency(aggregate.totalJobs > 0 ? aggregate.bookedRevenue / aggregate.totalJobs : 0)}
          />
          {aggregate.totalMaterialCost != null && (
            <StatCard
              label="Material Cost"
              value={this.formatCurrency(aggregate.totalMaterialCost)}
              sublabel="this period"
            />
          )}
          {aggregate.totalMaterialCost != null && aggregate.bookedRevenue > 0 && (
            <StatCard
              label="Gross Margin"
              value={`${Math.round(((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) * 100)}%`}
              sublabel={this.formatCurrency(aggregate.bookedRevenue - aggregate.totalMaterialCost)}
              className={((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) >= 0.5 ? 'success' : ((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) >= 0.3 ? '' : 'warning'}
            />
          )}
        </div>

        {/* Conversion metrics */}
        <div className="overview-cards" style={{ marginBottom: 24 }}>
          <StatCard
            label="Conversion Rate"
            value={`${Math.round(aggregate.leadToPaidRate)}%`}
            sublabel="lead to paid"
            className={aggregate.leadToPaidRate >= 50 ? 'success' : ''}
          />
          <StatCard
            label="Lead to Paid"
            value={`${aggregate.avgDaysLeadToPaid} days`}
            sublabel="average"
          />
          {customers && (
            <StatCard
              label="Repeat Rate"
              value={`${Math.round(customers.repeatRate)}%`}
              sublabel={`${customers.repeat} of ${customers.total} customers`}
            />
          )}
        </div>

        {/* Donut Charts Row */}
        <div className="chart-grid">
          <ChartCard title="Revenue by Source">
            <DonutChart
              data={sourceData.map(s => ({ name: s.source, value: s.value }))}
              formatValue={(v) => this.formatCurrency(v)}
              colors={chartColors('retail', 'orange', 'green')}
            />
          </ChartCard>
          <ChartCard title={`Revenue by Service (Top ${serviceData.length})`}>
            <DonutChart
              data={serviceData}
              formatValue={(v) => this.formatCurrency(v)}
            />
          </ChartCard>
        </div>

        {/* Revenue Trend */}
        {dailyTrend && dailyTrend.length > 0 && (
          <ChartCard title={`Revenue Trend (${this.state.period} days)`} className="trend-chart-card">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyTrend}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColor('blue')} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={chartColor('blue')} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: themeColor('--color-text-muted', '#9ca3af') }} // theme-ignore-line
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: themeColor('--color-text-muted', '#9ca3af') }} // theme-ignore-line
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      background: themeColor('--color-bg-card', '#fff'), // theme-ignore-line
                      border: `1px solid ${themeColor('--color-border', '#e5e7eb')}`, // theme-ignore-line
                      color: themeColor('--color-text-primary', '#1f2937'), // theme-ignore-line
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={chartColor('blue')}
                    fill="url(#revenueGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {/* Pipeline Speed + Insights */}
        <div className="bottom-grid">
          <ChartCard title="Pipeline Speed">
            {(data.pipelineAge || []).length > 0 ? (
              <>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                  Average days between stages this period
                </div>
                {data.pipelineAge.map((stage, i) => {
                  const maxDays = Math.max(...data.pipelineAge.map(s => s.avgDays));
                  const widthPct = maxDays > 0 ? (stage.avgDays / maxDays) * 100 : 0;
                  const color = this.getPipelineStageColor(stage.stage, stage.avgDays);
                  return (
                    <div key={i} className="pipeline-stage">
                      <div className="pipeline-stage-label">{stage.stage}</div>
                      <div className="pipeline-stage-bar-wrap">
                        <div className="pipeline-stage-bar" style={{ width: `${Math.max(widthPct, 4)}%`, backgroundColor: color }} />
                      </div>
                      <div className="pipeline-stage-value">
                        {stage.avgDays % 1 === 0 ? stage.avgDays : stage.avgDays.toFixed(1)}d
                      </div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 10, textAlign: 'center' }}>
                  Total avg: {data.pipelineAge.reduce((s, st) => s + st.avgDays, 0).toFixed(1)} days lead to paid
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
                Not enough data for this period
              </div>
            )}
          </ChartCard>

          {isOwner && (
            <ChartCard title="Insights">
              {(outstanding?.count || 0) > 0 && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ color: 'var(--color-warning)' }} /></span>
                  <div>
                    <div>{outstanding.count} job{outstanding.count > 1 ? 's' : ''} awaiting payment</div>
                    <div className="insight-value">{this.formatCurrency(outstanding.totalRevenue)} outstanding</div>
                  </div>
                </div>
              )}
              {outstanding?.jobs?.some(j => j.days_since_finished >= 7) && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ color: 'var(--color-danger)' }} /></span>
                  <div>
                    <div>{outstanding.jobs.filter(j => j.days_since_finished >= 7).length} job{outstanding.jobs.filter(j => j.days_since_finished >= 7).length > 1 ? 's' : ''} unpaid &gt;7 days</div>
                    <div className="insight-value">
                      {this.formatCurrency(outstanding.jobs.filter(j => j.days_since_finished >= 7).reduce((s, j) => s + j.request_total, 0))} at risk
                    </div>
                  </div>
                </div>
              )}
              {topSource && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-arrow-trend-up" style={{ color: 'var(--color-success)' }} /></span>
                  <div>
                    <div>Top Source: {topSource[0]}</div>
                    <div className="insight-value">{this.formatCurrency(topSource[1].revenue)} revenue</div>
                  </div>
                </div>
              )}
              {leadAttribution && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-bolt" style={{ color: 'var(--color-purple-dark)' }} /></span>
                  <div>
                    <div>{Object.keys(leadAttribution.bySource || {}).length} lead sources active</div>
                    <div className="insight-value">{Math.round(leadAttribution.conversionRate)}% conversion rate</div>
                  </div>
                </div>
              )}
              {data.reviews?.rating && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-star" style={{ color: 'var(--color-warning)' }} /></span>
                  <div>
                    <div>{data.reviews.rating} stars ({data.reviews.totalReviews} reviews)</div>
                    <div className="insight-value">Google rating</div>
                  </div>
                </div>
              )}
            </ChartCard>
          )}
        </div>

        {/* Lead Sources + Awaiting Payment */}
        {(leadSourceData.length > 0 || agingBuckets.length > 0) && (
          <div className="chart-grid">
            {leadSourceData.length > 0 && (
              <ChartCard title="Lead Sources">
                <DonutChart
                  data={leadSourceData}
                  showPercent={true}
                />
              </ChartCard>
            )}
            {agingBuckets.length > 0 && (
              <ChartCard title="Awaiting Payment">
                <DonutChart
                  data={agingBuckets}
                  formatValue={(v) => this.formatCurrency(v)}
                  showPercent={false}
                />
              </ChartCard>
            )}
          </div>
        )}

        {leadTrend.rows.length >= 2 && this.renderLeadSourceTrend(leadTrend)}

        {/* Inventory Alerts + Top Materials.
            Gated on the shop's materials tracking mode, which the server sends
            on this payload precisely because an empty array meant two opposite
            things and nothing distinguished them.

            Stock alerts are shown ONLY to a shop that maintains stock. For
            anyone else the panel is wrong in one of two directions, and both are
            live today: a shop with products but no stock rows sees every product
            flagged low, permanently, because available_qty resolves to 0 and the
            query filters on <= 0 — so zero-stock products lead the list rather
            than falling out of it. A shop with no products at all gets the
            opposite lie, a green "All products stocked" tick about stock it does
            not have.

            Hiding the SECTION rather than trusting an empty array is the point:
            suppressing only the populated list would leave the zero-state
            reassurance in place. */}
        {data.inventory && materialsMode !== 'none' && (showStockAlerts || hasMaterialsUsed) && (
          <div className="chart-grid">
            {showStockAlerts && (
            <ChartCard title="Inventory Alerts">
              {(data.inventory.lowStockAlerts || []).length === 0 ? (
                <div className="insight-item">
                  <span className="insight-icon">
                    <i className="fa-sharp fa-solid fa-circle-check" style={{ color: 'var(--color-success)' }} />
                  </span>
                  <div>All products stocked</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    {data.inventory.lowStockAlerts.length} product{data.inventory.lowStockAlerts.length !== 1 ? 's' : ''} below reorder point
                  </div>
                  {data.inventory.lowStockAlerts.map(item => (
                    <div className="insight-item" key={item.product_id}>
                      <span className="insight-icon">
                        <i className="fa-sharp fa-solid fa-triangle-exclamation"
                           style={{ color: item.current_qty === 0 ? 'var(--color-danger)' : 'var(--color-warning)' }} />
                      </span>
                      <div>
                        <div>{item.brand} {item.name}</div>
                        <div className="insight-value">
                          {item.current_qty} remaining (reorder at {item.reorder_point})
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </ChartCard>
            )}

            {hasMaterialsUsed && (
              <ChartCard title="Top Materials Used">
                <DonutChart
                  data={data.inventory.topMaterialsUsed.map(m => ({
                    name: m.category ? `${m.category} - ${m.name}` : m.name,
                    value: m.usage_count
                  }))}
                  showPercent={true}
                  colors={chartColors('blue', 'orange', 'green', 'purple', 'retail')}
                />
              </ChartCard>
            )}
          </div>
        )}

        {/* Product Sales */}
        {data.productSales && data.productSales.count > 0 && (
          <div className="chart-grid">
            <ChartCard title="Product Sales">
              <div className="d-flex" style={{ gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                <div><div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Revenue</div><div style={{ fontSize: 20, fontWeight: 700 }}>{this.formatCurrency(data.productSales.revenue)}</div></div>
                <div><div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Margin</div><div style={{ fontSize: 20, fontWeight: 700 }}>{this.formatCurrency(data.productSales.margin)} ({Math.round(data.productSales.marginPercent || 0)}%)</div></div>
                <div><div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Sales</div><div style={{ fontSize: 20, fontWeight: 700 }}>{data.productSales.count}</div></div>
                <div><div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Avg Sale</div><div style={{ fontSize: 20, fontWeight: 700 }}>{this.formatCurrency(data.productSales.avgSale)}</div></div>
              </div>
              {(data.productSales.topProducts || []).length > 0 && (
                <table className="jv-table jv-table-sm" style={{ width: '100%' }}>
                  <thead><tr><th>Product</th><th style={{ textAlign: 'right' }}>Sold</th><th style={{ textAlign: 'right' }}>Revenue</th></tr></thead>
                  <tbody>
                    {data.productSales.topProducts.slice(0, 5).map((p, i) => (
                      <tr key={i}>
                        <td>{p.brand ? `${p.brand} - ${p.name}` : p.name}</td>
                        <td style={{ textAlign: 'right' }}>{p.qtySold}</td>
                        <td style={{ textAlign: 'right' }}>{this.formatCurrency(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ChartCard>
          </div>
        )}

        {/* Campaigns + Payment Methods */}
        {(campaignData.length > 0 || paymentData.length > 0) && (
          <div className="chart-grid">
            {campaignData.length > 0 && (
              <ChartCard title="Campaign Performance">
                <DonutChart
                  data={campaignData}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={chartColors('purple', 'blue', 'orange', 'green')}
                />
              </ChartCard>
            )}
            {paymentData.length > 0 && (
              <ChartCard title="Payment Methods">
                <DonutChart
                  data={paymentData.map(p => ({ name: p.method, value: p.value }))}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={chartColors('retail', 'orange', 'green', 'purple')}
                />
              </ChartCard>
            )}
          </div>
        )}

        {/* Technician Performance */}
        {data.technicians && data.technicians.length > 0 && isOwner && (
          <ChartCard title="Technician Performance">
            <div className="tech-grid">
              <div className="tech-chart">
                <DonutChart
                  data={data.technicians.map(t => ({
                    name: t.name,
                    value: (t.lead_revenue_period ?? t.leadRevenuePeriod ?? t.revenuePeriod ?? 0)
                  }))}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={chartColors('blue', 'orange', 'green', 'purple', 'retail', 'amber')}
                />
              </div>
              <div className="tech-table-wrap">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Technician</th>
                      <th title="Services where this tech was the lead. A tech can complete steps on services they don't lead — see Tasks completed.">
                        Lead services
                      </th>
                      <th title="Tasks (steps) finished by this tech across all services in the period.">
                        Tasks completed
                      </th>
                      <th>Task hours</th>
                      <th>Clocked hours</th>
                      <th title="Revenue from services where this tech was the lead.">
                        Lead revenue
                      </th>
                      {data.technicians.some(t => t.tasks_completed_share_pct != null || t.tasksCompletedSharePct != null) && (
                        <th title="Share of all tasks completed across the team in this period.">
                          Contribution
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const showContribution = data.technicians.some(
                        t => t.tasks_completed_share_pct != null || t.tasksCompletedSharePct != null
                      );
                      return data.technicians.map(t => {
                        const leadServices = t.lead_services_period ?? t.leadServicesPeriod ?? t.completedPeriod ?? 0;
                        const leadRevenue = t.lead_revenue_period ?? t.leadRevenuePeriod ?? t.revenuePeriod ?? 0;
                        const tasksCompleted = t.completed_tasks ?? t.completedTasks ?? 0;
                        const taskHours = t.job_hours_period ?? t.jobHoursPeriod;
                        const clockedHours = t.clocked_hours_period ?? t.clockedHoursPeriod;
                        const sharePct = t.tasks_completed_share_pct ?? t.tasksCompletedSharePct;
                        return (
                          <tr key={t.id}>
                            <td style={{ fontWeight: 500 }}>{t.name}</td>
                            <td>{leadServices}</td>
                            <td>{tasksCompleted}</td>
                            <td>{taskHours != null ? `${Number(taskHours).toFixed(1)}h` : '-'}</td>
                            <td>{clockedHours != null ? `${Number(clockedHours).toFixed(1)}h` : '-'}</td>
                            <td>{this.formatCurrency(leadRevenue)}</td>
                            {showContribution && (
                              <td>{sharePct != null ? `${Number(sharePct).toFixed(0)}%` : '-'}</td>
                            )}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </ChartCard>
        )}
      </>
    );
  }

  // --- Dealer View ---
  // Story split into two streams:
  //   Pre-load = dealer prepping vehicle inventory (no end customer, no margin to capture)
  //   Sale    = customer-attached add-on (real retail price, real margin)
  // Signal comes from the server: aggregate.preload / aggregate.sale + per-row preload/sale on serviceBreakdown.
  renderDealerDashboard() {
    const { data } = this.state;
    if (!data) return null;

    const { aggregate, dailyTrend, serviceBreakdown, statusCounts } = data;

    // Request-level split (server-supplied).
    const saleRequests = aggregate.sale?.count || 0;
    const saleWholesale = aggregate.sale?.revenue || 0;
    const preloadRequests = aggregate.preload?.count || 0;
    const preloadWholesale = aggregate.preload?.revenue || 0;
    const totalRequests = aggregate.totalJobs || 0;

    // Sale retail comes from serviceBreakdown sub-objects (server doesn't return aggregate sale.retail).
    const saleRetail = (serviceBreakdown || []).reduce((s, svc) => s + (svc.sale?.retail || 0), 0);
    const saleMargin = saleRetail - saleWholesale;
    const saleMarginPct = saleRetail > 0 ? Math.round((saleMargin / saleRetail) * 100) : 0;

    // Build per-category breakdowns for both streams. Sale-margin-by-category drops zero-margin categories.
    const saleByCategory = {};
    const preloadByCategory = {};
    const saleMarginByCategory = {};
    (serviceBreakdown || []).forEach(svc => {
      const cat = svc.service_category || 'Other';
      const saleCount = svc.sale?.count || 0;
      const preloadCount = svc.preload?.count || 0;
      const saleRet = svc.sale?.retail || 0;
      const saleRev = svc.sale?.revenue || 0;
      const margin = saleRet - saleRev;

      if (saleCount > 0) {
        saleByCategory[cat] = saleByCategory[cat] || { name: cat, value: 0 };
        saleByCategory[cat].value += saleCount;
      }
      if (preloadCount > 0) {
        preloadByCategory[cat] = preloadByCategory[cat] || { name: cat, value: 0 };
        preloadByCategory[cat].value += preloadCount;
      }
      if (margin > 0) {
        saleMarginByCategory[cat] = saleMarginByCategory[cat] || { name: cat, value: 0, retail: 0 };
        saleMarginByCategory[cat].value += margin;
        saleMarginByCategory[cat].retail += saleRet;
      }
    });
    const saleByCategoryData = Object.values(saleByCategory).sort((a, b) => b.value - a.value);
    const preloadByCategoryData = Object.values(preloadByCategory).sort((a, b) => b.value - a.value);
    const saleMarginByCategoryData = Object.values(saleMarginByCategory)
      .sort((a, b) => b.value - a.value)
      .map(c => ({
        name: c.name,
        value: c.value,
        sublabel: c.retail > 0 ? `${Math.round((c.value / c.retail) * 100)}% margin` : ''
      }));

    // Where Are My Jobs? — driven by current_state histogram (replaces the misleading appointments.scheduled signal).
    const STATUS_LABELS = {
      awaiting_confirmation: 'Awaiting Confirmation',
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      started: 'In Progress',
      received: 'Received',
      completed: 'Completed',
      reviewed: 'Reviewed',
      collected: 'Collected',
      paid: 'Paid'
    };
    const statusColors = () => ({
      awaiting_confirmation: chartColor('amber'),
      scheduled: chartColor('blue'),
      in_progress: chartColor('purple'),
      started: chartColor('purple'),
      received: chartColor('orange'),
      completed: chartColor('green'),
      reviewed: chartColor('green'),
      collected: chartColor('retail'),
      paid: chartColor('green')
    });
    const statusColorMap = statusColors();
    const titleCase = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const statusData = Object.entries(statusCounts || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({
        name: STATUS_LABELS[k] || titleCase(k),
        value: v,
        color: statusColorMap[k] || chartColor('grey')
      }));

    // Sales-only margin trend — apply the sale margin % to daily revenue.
    const marginTrend = (dailyTrend || []).map(d => ({
      ...d,
      margin: saleMarginPct > 0 ? Math.round((d.revenue * saleMarginPct) / 100) : 0
    }));

    return (
      <>
        {/* Hero KPIs — split between sales (the margin story) and pre-load (the spend story) */}
        <div className="overview-cards">
          <StatCard
            label="Requests"
            value={totalRequests}
            sublabel={`${saleRequests} sale${saleRequests !== 1 ? 's' : ''} · ${preloadRequests} pre-load`}
            sparkData={this.buildSparkFromTrend(dailyTrend, 'jobs')}
          />
          <StatCard
            label="Customer Sales"
            value={this.formatCurrency(saleRetail)}
            sublabel={`${saleRequests} job${saleRequests !== 1 ? 's' : ''} · ${saleMarginPct}% margin`}
          />
          <StatCard
            label="Sales Margin"
            value={this.formatCurrency(saleMargin)}
            sublabel={`on ${this.formatCurrency(saleWholesale)} wholesale`}
            className="success"
            sparkData={this.buildSparkFromTrend(marginTrend, 'margin')}
          />
          <StatCard
            label="Pre-load Spend"
            value={this.formatCurrency(preloadWholesale)}
            sublabel={`${preloadRequests} job${preloadRequests !== 1 ? 's' : ''} prepped`}
          />
        </div>

        {/* Category split: sales vs pre-load, both by line count */}
        <div className="chart-grid">
          <ChartCard title="Customer Sales by Category">
            {saleByCategoryData.length > 0 ? (
              <DonutChart
                data={saleByCategoryData}
                showPercent={true}
                colors={chartColors('orange', 'blue', 'retail', 'purple', 'green')}
              />
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
                No customer sales in this period
              </div>
            )}
          </ChartCard>
          <ChartCard title="Pre-load by Category">
            {preloadByCategoryData.length > 0 ? (
              <DonutChart
                data={preloadByCategoryData}
                showPercent={true}
                colors={chartColors('grey', 'blue', 'purple', 'orange', 'green')}
              />
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
                No pre-load work in this period
              </div>
            )}
          </ChartCard>
        </div>

        {/* Sales margin by category + real status distribution */}
        <div className="chart-grid">
          <ChartCard title="Sales Margin by Category">
            {saleMarginByCategoryData.length > 0 ? (
              <DonutChart
                data={saleMarginByCategoryData}
                formatValue={(v) => this.formatCurrency(v)}
                colors={chartColors('green', 'retail', 'orange', 'purple', 'blue')}
              />
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
                No sales margin captured yet
              </div>
            )}
          </ChartCard>
          <ChartCard title="Where Are My Jobs?">
            {statusData.length > 0 ? (
              <DonutChart
                data={statusData}
                showPercent={true}
              />
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
                No jobs in this period
              </div>
            )}
          </ChartCard>
        </div>

        {/* Sales margin trend */}
        {marginTrend.length > 0 && (
          <ChartCard title={`Sales Margin Trend (${this.state.period} days)`} className="trend-chart-card">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={marginTrend}>
                  <defs>
                    <linearGradient id="dealerMarginGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColor('green')} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={chartColor('green')} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: themeColor('--color-text-muted', '#9ca3af') }} // theme-ignore-line
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: themeColor('--color-text-muted', '#9ca3af') }} // theme-ignore-line
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={{
                      background: themeColor('--color-bg-card', '#fff'), // theme-ignore-line
                      border: `1px solid ${themeColor('--color-border', '#e5e7eb')}`, // theme-ignore-line
                      color: themeColor('--color-text-primary', '#1f2937'), // theme-ignore-line
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Margin']}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="margin"
                    stroke={chartColor('green')}
                    fill="url(#dealerMarginGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {/* Specialist Activity */}
        {data.specialists && data.specialists.length > 0 && (
          <ChartCard title="Specialist Activity">
            <div className="tech-grid">
              <div className="tech-chart">
                <DonutChart
                  data={data.specialists.map(s => ({
                    name: s.name,
                    value: s.activeJobs + s.completedJobs
                  }))}
                  showPercent={true}
                  colors={chartColors('blue', 'orange', 'green', 'purple', 'retail', 'amber')}
                />
              </div>
              <div className="tech-table-wrap">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Specialist</th>
                      <th>Active</th>
                      <th>Completed</th>
                      <th>Your Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.specialists.map(s => (
                      <tr key={s.installer_id}>
                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                        <td>
                          {s.activeJobs > 0 ? (
                            <span style={{ color: 'var(--color-brand-blue)', fontWeight: 600 }}>{s.activeJobs}</span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>0</span>
                          )}
                        </td>
                        <td>{s.completedJobs}</td>
                        <td>{this.formatCurrency(s.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ChartCard>
        )}
      </>
    );
  }

  // --- Helpers ---

  getPipelineStageColor(stage, days) {
    const thresholds = {
      'Lead → Estimate':       { green: 1, yellow: 3 },
      'Estimate → Scheduled':  { green: 3, yellow: 7 },
      'Scheduled → Completed': { green: 3, yellow: 7 },
      'Completed → Paid':      { green: 1, yellow: 3 }
    };
    const t = thresholds[stage] || { green: 2, yellow: 5 };
    if (days <= t.green) return 'var(--color-success)';
    if (days <= t.yellow) return 'var(--color-warning)';
    return 'var(--color-danger)';
  }

  formatCurrency(val) {
    if (val == null) return '$0';
    if (val >= 1000) return `$${(val / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `$${Math.round(val).toLocaleString()}`;
  }

  capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Convert an object like { "Stripe": { count: 5, amount: 2000 } }
   * to an array like [{ method: "Stripe", count: 5, value: 2000 }]
   */
  // Stacked monthly bars, one segment per lead source, in the donut's colours.
  renderLeadSourceTrend({ rows, series }) {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatMonth = (ym) => {
      const [y, m] = ym.split('-');
      return `${MONTHS[Number(m) - 1]} '${y.slice(2)}`;
    };
    const muted = themeColor('--color-text-muted', '#9ca3af'); // theme-ignore-line
    const primary = themeColor('--color-text-primary', '#1f2937'); // theme-ignore-line
    const cardBg = themeColor('--color-bg-card', '#fff'); // theme-ignore-line
    const border = themeColor('--color-border', '#e5e7eb'); // theme-ignore-line

    return (
      <ChartCard title="Lead Source Trend (leads per month)" className="trend-chart-card">
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barCategoryGap="25%">
              <CartesianGrid vertical={false} stroke={border} />
              <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11, fill: muted }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: muted }} width={32} />
              <Tooltip
                cursor={{ fill: themeColor('--color-bg-hover', '#f3f4f6') }} // theme-ignore-line
                contentStyle={{ background: cardBg, border: `1px solid ${border}`, color: primary }}
                itemStyle={{ color: primary }}
                labelFormatter={formatMonth}
                formatter={(value, name) => [`${value} lead${value === 1 ? '' : 's'}`, name]}
              />
              <Legend
                iconType="square"
                iconSize={10}
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => <span style={{ color: primary }}>{value}</span>}
              />
              {series.map(s => (
                <Bar
                  key={s.label}
                  dataKey={s.label}
                  stackId="leads"
                  fill={chartColor(s.colorKey)}
                  stroke={cardBg}
                  strokeWidth={1}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {this.state.period < 180 && (
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6, textAlign: 'center' }}>
            Covers the selected period — choose 180d or 365d to see a longer trend
          </div>
        )}
      </ChartCard>
    );
  }

  objectToArray(obj, keyName, valueMap) {
    if (!obj) return [];
    return Object.entries(obj).map(([key, val]) => {
      const item = { [keyName]: key };
      Object.entries(valueMap).forEach(([srcKey, destKey]) => {
        item[destKey] = val[srcKey] || 0;
      });
      return item;
    });
  }

  /**
   * Build aging buckets from outstanding jobs array
   * Each job has: { request_id, request_total, days_since_finished }
   */
  buildAgingBuckets(jobs) {
    const buckets = [
      { name: 'Today', value: 0, color: chartColor('green') },
      { name: '1-3 days', value: 0, color: chartColor('blue') },
      { name: '4-7 days', value: 0, color: chartColor('amber') },
      { name: '7+ days', value: 0, color: chartColor('red') }
    ];
    jobs.forEach(j => {
      if (j.days_since_finished == null) return; // skip jobs with no finish date
      const d = j.days_since_finished;
      if (d === 0) buckets[0].value += j.request_total || 0;
      else if (d <= 3) buckets[1].value += j.request_total || 0;
      else if (d <= 7) buckets[2].value += j.request_total || 0;
      else buckets[3].value += j.request_total || 0;
    });
    return buckets.filter(b => b.value > 0);
  }

  buildSparkFromTrend(trend, key) {
    if (!trend || trend.length < 2) return null;
    const step = Math.max(1, Math.floor(trend.length / 10));
    return trend.filter((_, i) => i % step === 0 || i === trend.length - 1).map(t => ({ value: t[key] || 0 }));
  }

  // --- Mock Data (matches server response shape) ---
  getMockData() {
    const days = this.state.period;
    const dailyTrend = [];
    const now = new Date();
    for (let i = days; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const rev = Math.round(400 + Math.random() * 800);
      dailyTrend.push({
        date: d.toISOString().split('T')[0],
        revenue: rev,
        jobs: Math.round(1 + Math.random() * 3)
      });
    }

    return {
      period: { days, dealerId: null },

      aggregate: {
        totalJobs: 142,
        leads: { today: 3, thisWeek: 12, thisMonth: 38, lastMonth: 45 },
        bookedRevenue: 28450,
        leadToPaidRate: 67.61,
        avgDaysLeadToPaid: 8.34
      },

      appointments: {
        scheduled: 96,
        noShowRate: 4.17,
        rescheduleRate: 12.50
      },

      inProgress: {
        avgHoursInProgress: 3.75
      },

      outstanding: {
        count: 5,
        totalRevenue: 3200,
        jobs: [
          { request_id: 1042, request_total: 850, request_type: 'installer', days_since_finished: 0 },
          { request_id: 1038, request_total: 720, request_type: 'dealer', days_since_finished: 2 },
          { request_id: 1035, request_total: 680, request_type: 'installer', days_since_finished: 3 },
          { request_id: 1029, request_total: 550, request_type: 'dealer', days_since_finished: 5 },
          { request_id: 1022, request_total: 400, request_type: 'installer', days_since_finished: 9 }
        ]
      },

      revenueBySource: {
        Dealer: { jobs: 45, revenue: 18200 },
        Retail: { jobs: 97, revenue: 42300 }
      },

      serviceBreakdown: [
        { service_name: 'Full Front PPF', service_category: 'PPF', total_revenue: 14200, total_retail: 16800, service_count: 28 },
        { service_name: 'Full Window Tint', service_category: 'Window Tint', total_revenue: 9800, total_retail: 12400, service_count: 35 },
        { service_name: 'Ceramic Coating', service_category: 'Ceramic', total_revenue: 5400, total_retail: 7200, service_count: 12 },
        { service_name: 'Full Body Wrap', service_category: 'Vinyl Wrap', total_revenue: 3800, total_retail: 5000, service_count: 4 },
        { service_name: 'Partial Front PPF', service_category: 'PPF', total_revenue: 2200, total_retail: 2800, service_count: 8 }
      ],

      payments: {
        byMethod: {
          Stripe: { count: 52, amount: 22400 },
          Check: { count: 8, amount: 4200 },
          Cash: { count: 3, amount: 1200 }
        },
        totalPaid: 27800
      },

      dailyTrend,

      reviews: {
        rating: 4.8,
        totalReviews: 247,
        lastSynced: '2026-02-02T07:00:00.000Z'
      },

      leadAttribution: {
        totalLeads: 85,
        convertedLeads: 34,
        conversionRate: 40,
        bySource: {
          Google: { total: 30, converted: 13, revenue: 7900 },
          'Organic (calls/texts)': { total: 20, converted: 10, revenue: 5200 },
          Facebook: { total: 15, converted: 5, revenue: 2800 },
          'Website Form': { total: 9, converted: 3, revenue: 1500 },
          'AI Assistant': { total: 3, converted: 1, revenue: 500 },
          Returning: { total: 8, converted: 2, revenue: 900 }
        },
        byMonth: {
          '2026-08': { Google: 12, 'Organic (calls/texts)': 8, Facebook: 6, 'Website Form': 3, Returning: 3 },
          '2026-09': { Google: 10, 'Organic (calls/texts)': 7, Facebook: 5, 'Website Form': 3, 'AI Assistant': 1, Returning: 3 },
          '2026-10': { Google: 8, 'Organic (calls/texts)': 5, Facebook: 4, 'Website Form': 3, 'AI Assistant': 2, Returning: 2 }
        }
      },

      pipelineAge: [
        { stage: 'Lead → Estimate', avgDays: 1.8, count: 85 },
        { stage: 'Estimate → Scheduled', avgDays: 4.2, count: 57 },
        { stage: 'Scheduled → Completed', avgDays: 2.1, count: 96 },
        { stage: 'Completed → Paid', avgDays: 3.5, count: 82 }
      ],

      inventory: {
        lowStockAlerts: [
          { product_id: 12, name: 'Ultimate Plus 60"', brand: 'XPEL', current_qty: 1, reorder_point: 5 },
          { product_id: 8, name: 'Color Stable 35%', brand: '3M', current_qty: 0, reorder_point: 3 },
          { product_id: 23, name: 'Ceramic IR 50%', brand: 'XPEL', current_qty: 2, reorder_point: 4 }
        ],
        topMaterialsUsed: [
          { name: 'XPEL Ultimate Plus 60"', category: 'PPF', usage_count: 14 },
          { name: '3M Color Stable 35%', category: 'Window Tint', usage_count: 9 },
          { name: 'XPEL Ceramic IR 50%', category: 'Window Tint', usage_count: 7 },
          { name: 'SunTek CXP 20%', category: 'Window Tint', usage_count: 5 },
          { name: 'XPEL Prime XR+', category: 'PPF', usage_count: 3 }
        ]
      },

      campaigns: [
        { campaign_id: 12, campaign_name: 'Spring PPF Promo', total_leads: 18, converted: 7, revenue: 4200, conversionRate: 38.89 },
        { campaign_id: 15, campaign_name: 'Tint Tuesday', total_leads: 8, converted: 3, revenue: 1200, conversionRate: 37.50 }
      ],

      customers: {
        total: 89,
        repeat: 12,
        repeatRate: 13.48
      },

      technicians: [
        { id: 45, name: 'Mike Thompson', activeServices: 2, completedPeriod: 18, avgServiceHours: 3.4, revenuePeriod: 8200, clockedHoursPeriod: 160.5, jobHoursPeriod: 128.4, utilizationPct: 80 },
        { id: 46, name: 'Steve Garcia', activeServices: 1, completedPeriod: 14, avgServiceHours: 3.8, revenuePeriod: 6400, clockedHoursPeriod: 155.0, jobHoursPeriod: 112.0, utilizationPct: 72 },
        { id: 47, name: 'Carlos Rivera', activeServices: 0, completedPeriod: 11, avgServiceHours: 4.1, revenuePeriod: 4800, clockedHoursPeriod: 148.0, jobHoursPeriod: 88.0, utilizationPct: 59 }
      ]
    };
  }

  render() {
    const { loading, error, data, period, isDesktop } = this.state;
    const { userType } = this.props;
    const isDealer = userType === 'dealer';

    return (
      <div className={`dashboard-container ${!isDesktop ? 'mobile' : ''}`}>
        {/* Header */}
        <div className="page-header">
          <div className="period-selector">
            {[7, 30, 90, 180, 365].map(d => (
              <button
                key={d}
                className={period === d ? 'active' : ''}
                onClick={() => this.handlePeriodChange(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {error && <div className="dashboard-error">{error}</div>}

        {loading && (
          <div className="dashboard-loading">
            <i className="fa-sharp fa-solid fa-spinner fa-spin" /> Loading dashboard...
          </div>
        )}

        {!loading && data && (
          isDealer ? this.renderDealerDashboard() : this.renderInstallerDashboard()
        )}
      </div>
    );
  }
}

export default Dashboard;
